import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';

import ModalOverlay from 'components/ui/Overlays/ModalOverlay/ModalOverlay';
import Button from 'components/ui/Button';
import Form, {FormOnSubmit, WithFormError} from 'components/ui/Form/Form/Form';
import useProhibitedActivitiesModal from './useProhibitedActivitiesModal';
import Checkbox from 'components/ui/Form/Checkbox/Checkbox';
import {PATH} from 'constants/common';
import {prohibitedActivitiesSchema} from 'form/validation/prohibitedActivitiesSchema';
import styles from './ProhibitedActivitesModal.module.scss';

interface IProhibitedActivitesModalFormValues {
    prohibitedActivities?: boolean;
}

const ProhibitedActivitesModal = () => {
    const {closeModal, history} = useProhibitedActivitiesModal();

    const DEFAULT_FORM_VALUES: IProhibitedActivitesModalFormValues = {
        prohibitedActivities: false,
    };

    const formMethods = useForm<
        WithFormError<IProhibitedActivitesModalFormValues>
    >({
        defaultValues: DEFAULT_FORM_VALUES,
        resolver: yupResolver(prohibitedActivitiesSchema),
    });
    const {watch} = formMethods;
    const prohibitedActivities = watch('prohibitedActivities');

    const onSubmit: FormOnSubmit<IProhibitedActivitesModalFormValues> = () => {
        closeModal();
    };

    const onCancel = () => {
        closeModal();
        history.push(PATH.DASHBOARD);
    };

    return (
        <ModalOverlay
            doNotCloseOnOutsideClick
            scrollable={false}
            closeIcon={false}
        >
            <div className={styles.container}>
                <Form<IProhibitedActivitesModalFormValues>
                    className={styles.formContainer}
                    onSubmit={onSubmit}
                    formMethods={formMethods}
                >
                    <div className={styles.title}>
                        <h3>
                            Prohibited Business Activities and Payment Flows
                        </h3>
                    </div>
                    <p>
                        To maintain a safe and compliant environment, amnis
                        cannot open accounts for businesses involved in certain
                        prohibited activities due to AML (Anti-Money
                        Laundering), regulatory requirements, and internal
                        policies.
                    </p>
                    <div>
                        <ul>
                            <li>
                                Groups, entities, or individuals covered by UN,
                                EU or other sanctions
                            </li>
                            <li>
                                Businesses domiciled or banking in jurisdictions
                                designated non-cooperative by the FATF
                            </li>
                            <li>
                                Shell companies/banks (entities with no physical
                                presence for meaningful decision-making or
                                operations)
                            </li>
                            <li>Bearer share corporations</li>
                            <li>Unregistered charities or NGOs</li>
                            <li>Carbon credit businesses</li>
                            <li>Political organizations </li>
                            <li>Pawnbrokers</li>
                            <li>
                                Gambling and related activities (e.g., online
                                betting, casinos)
                            </li>
                            <li>
                                Entities without appropriate licenses as
                                required by law in their operating countries
                            </li>
                            <li>
                                Businesses involved in illegal goods or
                                services, including counterfeit goods, trademark
                                infringement, human trafficking, child labor,
                                and prostitution
                            </li>
                            <li>
                                Multi-Level Marketing (MLM) or Ponzi/pyramid
                                schemes
                            </li>
                            <li>Investment companies</li>
                            <li>
                                Transactions unrelated to commercial activities
                                or speculative trading
                            </li>
                        </ul>
                    </div>
                    <div>
                        <div>
                            <Checkbox
                                name="prohibitedActivities"
                                afterLabel="I declare that my business does not engage in any of the prohibited business activities listed above. I confirm that I have provided accurate information, and I understand that inaccurate information could result in account termination."
                            />
                        </div>
                        <div className={styles.button}>
                            <Button
                                type="submit"
                                disabled={!prohibitedActivities}
                            >
                                Agree and continue
                            </Button>
                            <Button variant="text" onClick={onCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>
        </ModalOverlay>
    );
};

export default ProhibitedActivitesModal;
